import { FunctionComponent, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { faCircleCheck, faCircleXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { ROUTE_CONFIG } from "@/routes/config";

/**
 * Result screen of the Salesforce OAuth flow.
 *
 * The backend exchanges the code itself and redirects here with `?success=` (+ `?error=` on
 * failure) — this page never talks to the API, unlike the legacy HubSpot code-exchange
 * callback. Every failure mode of the connect (an expired state, `redirect_uri_mismatch`,
 * `invalid_grant`, an unreachable identity URL) reaches the customer only through that `error`
 * parameter, so it is rendered verbatim rather than replaced by a generic sentence.
 */
export const SalesforceCallback: FunctionComponent = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const success = searchParams.get("success");
	const error = searchParams.get("error");

	const isSuccess = success === "true";
	const status: "success" | "error" = isSuccess ? "success" : "error";

	const [countdown, setCountdown] = useState(3);

	useEffect(() => {
		if (status !== "success") {
			return;
		}

		const timer = setInterval(() => {
			setCountdown((prev) => {
				if (prev <= 1) {
					clearInterval(timer);
					navigate(ROUTE_CONFIG.INTEGRATIONS_SALESFORCE);

					return 0;
				}

				return prev - 1;
			});
		}, 1000);

		return () => clearInterval(timer);
	}, [status, navigate]);

	return (
		<div className="flex h-screen w-full items-center justify-center bg-gray-50 p-4">
			<div className="flex w-[480px] max-w-full flex-col items-center justify-center rounded-[20px] bg-white p-8 shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)]">
				{status === "success" && (
					<>
						<Icon className="mb-4 h-[48px] w-[48px] text-status-success" icon={faCircleCheck} />
						<div className="mb-2 text-center text-md font-bold">
							Salesforce Connected Successfully!
						</div>
						<div className="mb-6 text-center text-ssm text-gray-500">
							Your Salesforce org has been connected. Redirecting to settings in {countdown}s...
						</div>
						<div className="flex w-full max-w-[200px] justify-center">
							<Button
								color={ButtonColor.ACTION}
								size={ButtonSize.S}
								testId="salesforce-callback-settings"
								title="Go to Settings"
								onClick={() => navigate(ROUTE_CONFIG.INTEGRATIONS_SALESFORCE)}
							/>
						</div>
					</>
				)}

				{status === "error" && (
					<>
						<Icon className="mb-4 h-[48px] w-[48px] text-status-error" icon={faCircleXmark} />
						<div className="mb-2 text-center text-md font-bold">Connection Failed</div>
						<div className="mb-6 text-center text-ssm text-gray-500">
							{error || "Something went wrong while connecting to Salesforce. Please try again."}
						</div>
						<div className="flex w-full max-w-[200px] justify-center">
							<Button
								color={ButtonColor.ACTION}
								size={ButtonSize.S}
								testId="salesforce-callback-retry"
								title="Try Again"
								onClick={() => navigate(ROUTE_CONFIG.INTEGRATIONS_SALESFORCE)}
							/>
						</div>
					</>
				)}
			</div>
		</div>
	);
};
